import { Maximize2, X } from "lucide-react";
import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";

import type { TicketSummary } from "../api/types";
import { useHotkeys, useMediaQuery } from "../lib/hotkeys";
import { SPLIT_QUERY, type FromList } from "./split";
import { TicketView } from "./TicketPage";

/**
 * Split view (DECISIONS #53): on a wide screen, opening a ticket from a table shows it beside
 * the list instead of leaving it. The open ticket is in the address (?open=ASP-000123), so
 * reloading, Back and sharing the link all work. J/K move through the list, Enter opens,
 * Escape closes.
 */

export interface TicketPeek {
  /** The ticket shown beside the list, if any (only on wide screens). */
  openRef: string | null;
  /** The row the keyboard is on. */
  activeRef: string | null;
  /** Show this ticket beside the list. False on narrow screens: follow the link instead. */
  open: (reference: string) => boolean;
  close: () => void;
}

export function useTicketPeek(tickets: TicketSummary[]): TicketPeek {
  const wide = useMediaQuery(SPLIT_QUERY);
  const [params, setParams] = useSearchParams();
  const linked = params.get("open");
  const openRef = wide ? linked : null;
  const [active, setActive] = useState<string | null>(null);
  const activeRef = openRef ?? active;

  const show = useCallback(
    (reference: string | null) => {
      setParams(
        (current) => {
          if (reference) current.set("open", reference);
          else current.delete("open");
          return current;
        },
        { replace: Boolean(linked) && Boolean(reference) },
      );
    },
    [setParams, linked],
  );

  const open = useCallback(
    (reference: string) => {
      if (!wide) return false;
      setActive(reference);
      show(reference);
      return true;
    },
    [wide, show],
  );
  const close = useCallback(() => show(null), [show]);

  function move(step: number) {
    if (!tickets.length) return;
    const index = tickets.findIndex((t) => t.reference === activeRef);
    const next = tickets[index === -1 ? 0 : Math.min(tickets.length - 1, Math.max(0, index + step))];
    if (!next) return;
    setActive(next.reference);
    if (openRef) show(next.reference);
    const row = document.querySelector(`[data-ticket-row="${next.reference}"]`);
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
  }

  useHotkeys({
    j: () => move(1),
    k: () => move(-1),
    Enter: () => {
      if (activeRef && !openRef) open(activeRef);
    },
    Escape: () => (openRef ? close() : undefined),
  });

  return { openRef, activeRef, open, close };
}

/** Space kept below a pane that reaches the bottom of the window, in px (--space-4). */
const PANE_BOTTOM_GAP = 16;

/**
 * The pane scrolls with the page, so the page has one scrollbar. A pane taller than the window
 * moves with the page until its far end is in view, then holds there; scrolling back reverses
 * it. So all of a long ticket can be reached beside a long list. A new ticket starts at its top,
 * and if the pane isn't wholly in view (the page above pushes it down), the page scrolls to it.
 */
function useStickyPane(reference: string): RefObject<HTMLElement | null> {
  const pane = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = pane.current;
    if (!el) return;
    // Where the stylesheet holds it (--pane-top, in tickets.css), read afresh each time: a sticky
    // header above it can still be measuring itself when the pane opens.
    const pinnedTop = () => parseFloat(getComputedStyle(el).getPropertyValue("--pane-top")) || 0;
    let top = pinnedTop();
    let lastY = window.scrollY;
    const place = () => {
      const pinned = pinnedTop();
      const lowest = Math.min(pinned, window.innerHeight - el.offsetHeight - PANE_BOTTOM_GAP);
      top = Math.min(pinned, Math.max(lowest, top - (window.scrollY - lastY)));
      lastY = window.scrollY;
      el.style.top = `${top}px`;
    };
    place();
    const bottom = el.getBoundingClientRect().bottom;
    if (bottom > window.innerHeight && el.offsetHeight <= window.innerHeight) {
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      window.scrollBy({ top: bottom - window.innerHeight + PANE_BOTTOM_GAP, behavior: reduced ? "auto" : "smooth" });
    }
    window.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", place);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(place) : null;
    observer?.observe(el);
    return () => {
      window.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
      observer?.disconnect();
    };
  }, [reference]);
  return pane;
}

/** The open ticket, beside the list. */
export function PeekPane({ reference, onClose }: { reference: string; onClose: () => void }) {
  const pane = useStickyPane(reference);
  const location = useLocation();
  // The full page can come back here, to this list with this ticket open.
  const from: FromList = { from: `${location.pathname}${location.search}` };
  return (
    <aside ref={pane} className="peek-pane" aria-label={`Ticket ${reference}`}>
      <div className="peek-bar">
        <span className="peek-ref">{reference}</span>
        <span className="peek-actions">
          <Link to={`/tickets/${reference}`} state={from} className="icon-btn" aria-label="Open full page" data-tooltip="Open full page">
            <Maximize2 size={16} aria-hidden="true" />
          </Link>
          <button type="button" className="icon-btn" aria-label="Close ticket" data-tooltip="Close (Esc)" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </span>
      </div>
      <TicketView key={reference} reference={reference} embedded />
    </aside>
  );
}
